import {FC, useContext, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {
  applyLaborTemplate,
  applyScheduleWorkflowAction,
  autoFillSchedule,
  clearLaborSchedule,
  clearScheduleAssignments,
  copyFromPreviousWeek,
  getLaborScheduleActual,
  getLaborTemplates,
  getScheduleWorkflow,
  getWeeklyShifts,
  getPDFContent,
  uploadFromFile,
  validateSchedule,
} from '../core/_requests'
import LoadingStateContext from '../context/LoadingContext'
import {useStore} from '../store'
import FdsModalContext from '../../../../_fds/layout/ModalContext'
import {useToast} from '../../../../_fds/layout/components/toast-messages'
import Dropdown from 'react-bootstrap/Dropdown'
import {
  LaborAutofillResultModel,
  LaborAutofillWarningModel,
  LaborScheduleActualModel,
  LaborTemplateListItemModel,
  ScheduleWorkflowModel,
  WorkflowActionModel,
} from '../core/_models'
import {format, parseISO, startOfWeek} from 'date-fns'
import {convertLaborShifts, isUnassignedEventRowId} from '../helpers/generals'
import {useAuth, hasPermission, LABOR_SCHEDULING_PERMS} from '../../auth'
import {useRecommendedSchedule} from './proposal/useRecommendedSchedule'
import {SaveScheduleAsTemplateModal} from './template/SaveScheduleAsTemplateModal'
import {summarizeStaffingRules} from '../helpers/autofillRules'
import {ActionForm, SchedulerActionForm} from './SchedulerActionForm'
import {StatusPath} from './common/StatusPath'
import {WorkflowHistoryButton} from './common/WorkflowHistoryButton'
import {useStatusSlot} from './common/statusSlot'

type SchedulerDropdownProps = {
  laborScheduleActual: LaborScheduleActualModel | null
  setLaborScheduleActual: (laborScheduleActual: LaborScheduleActualModel | null) => void
}

// Machine reasons from the solver -> what a manager should read.
const AUTOFILL_WARNING_LABELS: Record<string, string> = {
  weekly_hours: 'Over weekly hours',
  daily_hours: 'Over daily hours',
  rest: 'Short rest between shifts',
  clopening: 'Closing then opening',
  consecutive_days: 'Too many days in a row',
  not_preferred: 'Not-preferred hours',
  staffing_rule: 'Staffing rule',
}

/**
 * Group auto-fill warnings by rule, so a week with nine overtime shifts reads
 * as one thing to review rather than nine separate problems.
 */
const groupAutofillWarnings = (
  warnings: LaborAutofillWarningModel[]
): Array<{reason: string; label: string; lines: string[]}> => {
  const byReason = new Map<string, string[]>()
  warnings.forEach((warning) => {
    const when = warning.start_time ? format(parseISO(warning.start_time), 'EEE HH:mm') : ''
    const lines = byReason.get(warning.reason) || []
    lines.push(when ? `${when} — ${warning.detail}` : warning.detail)
    byReason.set(warning.reason, lines)
  })
  return Array.from(byReason.entries()).map(([reason, lines]) => ({
    reason,
    label: AUTOFILL_WARNING_LABELS[reason] || reason,
    lines,
  }))
}

// Upload-from-file is hidden from the Options menu until there is an agreed
// file format to hand clients. The handler, the confirm dialog and the backend
// endpoint all stay wired up; flip this to show the option again.
const SHOW_UPLOAD_FROM_FILE = false

/* eslint-disable jsx-a11y/anchor-is-valid */
export const SchedulerDropdown: FC<SchedulerDropdownProps> = ({
  laborScheduleActual,
  setLaborScheduleActual,
}) => {
  const {setLoadingState} = useContext(LoadingStateContext)
  const {
    selectedLocation,
    selectedLocationId,
    handleState,
    week,
    selectedDate,
    strictlyWrongSchedule,
    selectedLaborScheduleId,
    events,
    templateMode,
    view,
  } = useStore()
  const {setSettings} = useContext(FdsModalContext)
  // The recommended-schedule prompt lives in the store, so the Options menu can
  // reopen it even though the toolbar is rendered outside the scheduler's tree.
  const {pendingProposal, openModal} = useRecommendedSchedule()
  const {currentUser} = useAuth()
  // Mirror the backend gates: copy-from/apply-template create shifts
  // (labor_scheduling.create); clear/upload replace existing ones
  // (labor_scheduling.edit). PDF download only needs view.
  const canCreateShifts = hasPermission(currentUser, LABOR_SCHEDULING_PERMS.create)
  const canEditShifts = hasPermission(currentUser, LABOR_SCHEDULING_PERMS.edit)
  // Auto-fill only has work to do while the week HAS unassigned shifts, and the
  // board already knows: each one owns a pseudo-resource row keyed by an
  // 'unassigned-' sentinel instead of a numeric employee_id. Counting here
  // keeps the menu honest without another round-trip.
  const unassignedCount = events.filter((event) => isUnassignedEventRowId(event.employee_id)).length
  const {showToast} = useToast()
  // The open input dialog for an action that needs one (a reason, a week to
  // copy from, a template, a file) — see SchedulerActionForm.
  const [form, setForm] = useState<ActionForm | null>(null)
  // null while loading, so the dialog can say so instead of "no templates".
  const [templates, setTemplates] = useState<LaborTemplateListItemModel[] | null>(null)
  // "Save schedule as template" collects a name and two choices, which is more
  // than the shared confirm modal can hold — it gets its own dialog.
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false)
  const [toolBusy, setToolBusy] = useState<string | null>(null)
  const toolBusyRef = useRef(false)

  // Server-computed approval workflow: the backend decides which actions
  // this user may take from the current status (plus pre-evaluated guard
  // results); the dropdown renders it verbatim and posts back action keys.
  const [workflow, setWorkflow] = useState<ScheduleWorkflowModel | null>(null)

  const {weekStartOn} = week!

  // Monotonic request sequence: workflow fetches are SLOW (guard evaluation
  // runs the validation rules server-side) and week switches fire them
  // back-to-back — without this, a stale response for the PREVIOUS week's
  // schedule can land last and pair another week's actions with the current
  // week's badge ("Sending to Approval" showing on an awaiting-approval week).
  const workflowReqSeqRef = useRef(0)
  // The schedule currently displayed — a response is only applied if it is
  // FOR this schedule. Issue-order alone is not enough: the debounced
  // shift-edit refresh can fire LAST with an id captured before a week
  // switch, making the stale-id request the newest one.
  const displayedScheduleIdRef = useRef<number | undefined>(laborScheduleActual?.id)
  displayedScheduleIdRef.current = laborScheduleActual?.id

  const refreshWorkflow = async (scheduleId: number) => {
    if (scheduleId !== displayedScheduleIdRef.current) {
      return // request for a schedule we no longer display
    }
    const seq = ++workflowReqSeqRef.current
    const wf = await getScheduleWorkflow(scheduleId)
    if (seq !== workflowReqSeqRef.current || scheduleId !== displayedScheduleIdRef.current) {
      return // superseded, or the displayed schedule changed mid-flight
    }
    setWorkflow(wf || null)
  }

  // The events array this component last fetched a workflow for. The debounced
  // refresh below is for shift EDITS; on a cold load (or a week switch) the
  // schedule id and its shifts arrive together, so both effects would fire for
  // the same state and the slow guard-evaluation endpoint would run twice.
  const workflowEventsRef = useRef<typeof events | null>(null)

  // Initial load + status changes: fetch IMMEDIATELY. This must not sit
  // behind the events-debounce below — `events` gets a new identity on every
  // store update, and a busy store would keep resetting the timer and starve
  // the fetch, leaving the actions select empty.
  // The schedule the current `workflow` belongs to.
  const workflowScheduleIdRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (!laborScheduleActual?.id || templateMode) {
      workflowReqSeqRef.current++ // invalidate any in-flight fetch
      setWorkflow(null)
      return
    }
    // Another schedule (a week or store switch): drop the previous one's
    // status and actions at once. The fetch is slow, and until it lands the
    // path shows this schedule's own status, with no actions to click.
    if (workflowScheduleIdRef.current !== laborScheduleActual.id) {
      workflowScheduleIdRef.current = laborScheduleActual.id
      setWorkflow(null)
    }
    workflowEventsRef.current = events
    refreshWorkflow(laborScheduleActual.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laborScheduleActual?.id, laborScheduleActual?.workflow_status, templateMode])

  // Shift edits: refresh the ⚠/blocked markers once per edit burst (guard
  // evaluation runs the validation rules server-side, so debounce). Only a
  // marker refresh — correctness comes from the click-time refetch. The id is
  // read from the ref AT FIRE TIME (a week switch also replaces events, and a
  // timer capturing the old week's id fired a stale-schedule request); the id
  // dep clears any pending timer on switch.
  useEffect(() => {
    if (!laborScheduleActual?.id || templateMode) {
      return
    }
    // Nothing has happened to the shifts since the fetch above observed them —
    // re-evaluating the same guards would be a pure duplicate.
    if (workflowEventsRef.current === events) {
      return
    }
    const timer = setTimeout(() => {
      const currentId = displayedScheduleIdRef.current
      if (currentId) {
        workflowEventsRef.current = events
        refreshWorkflow(currentId)
      }
    }, 800)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, laborScheduleActual?.id])

  // Status + actions must move together: resync BOTH from the server when we
  // detect drift (409, action vanished) — refreshing only the actions left the
  // badge/gating on the stale status. SEQUENTIAL on purpose: resolve the
  // schedule for the viewed week first, then fetch the workflow for THAT
  // schedule — fetching the workflow for the old id in parallel could pair
  // this week's badge with another week's actions.
  const resyncSchedule = async () => {
    const schedule = await getLaborScheduleActual(selectedLocationId, selectedDate)
    const scheduleId = schedule?.id ?? laborScheduleActual?.id
    if (schedule) {
      setLaborScheduleActual(schedule)
    }
    if (scheduleId) {
      await refreshWorkflow(scheduleId)
    }
  }

  // Latch against double submits: the modal's fade-out keeps its button
  // clickable, and a second POST would race the first (409 + drift).
  const actionInFlightRef = useRef(false)

  const performWorkflowAction = async (
    action: WorkflowActionModel,
    reason: string,
    acknowledgedWarnings: string[]
  ) => {
    if (!laborScheduleActual || actionInFlightRef.current) return
    actionInFlightRef.current = true
    setLoadingState(true)
    try {
      const res = await applyScheduleWorkflowAction(laborScheduleActual.id, action.key, {
        reason: reason || undefined,
        acknowledged_warnings: acknowledgedWarnings,
        expected_status: workflow?.status.key,
      })
      setLaborScheduleActual({
        ...laborScheduleActual,
        workflow_status: res.schedule.workflow_status,
      })
      // The action response is authoritative — invalidate in-flight fetches
      // so a slow stale one can't overwrite it.
      workflowReqSeqRef.current++
      setWorkflow(res.workflow)
      showToast('Schedule Updated', `${action.label} applied successfully!`, 'success')
    } catch (e: any) {
      const status = e?.response?.status
      const detail = e?.response?.data?.detail
      if (status === 409) {
        // Someone else moved the schedule — resync status AND options.
        showToast('Schedule Changed', 'The schedule status changed — options refreshed.', 'warning')
        resyncSchedule()
      } else if (status === 422 && detail?.code === 'warnings_not_acknowledged') {
        // Server-side warnings (may be fresher than the pre-evaluated ones).
        openWarningAcknowledgeModal(action, reason, detail.guards || [])
      } else if (status === 422 && detail?.code === 'guard_blocked') {
        showToast(
          'Action Blocked',
          (detail.guards || [])
            .map((g: {message: string | null}) => g.message)
            .filter(Boolean)
            .join(' ') || 'A blocking check failed.',
          'danger'
        )
        refreshWorkflow(laborScheduleActual.id)
      } else if (status === 422 && detail?.code === 'reason_required') {
        showToast('Reason Required', 'This action requires a reason.', 'danger')
      } else if (status === 403) {
        showToast('Not Allowed', 'You are not allowed to perform this action.', 'danger')
      } else {
        showToast('Error', 'Error updating schedule status!', 'danger')
      }
    } finally {
      actionInFlightRef.current = false
    }
    setDefaults()
    setTimeout(() => {
      setLoadingState(false)
    }, 10)
  }

  const openWarningAcknowledgeModal = (
    action: WorkflowActionModel,
    reason: string,
    guards: Array<{name: string; message: string | null}>
  ) => {
    setSettings({
      cancelButtonText: 'Cancel',
      saveButtonText: 'Proceed anyway',
      saveButtonCallback: () => {
        performWorkflowAction(
          action,
          reason,
          guards.map((g) => g.name)
        )
      },
      closeButtonCallback: () => {
        setDefaults()
      },
      titleText: action.label,
      content: (
        <div>
          <div className='text-danger fs-6 fw-bold mb-3'>Some checks did not pass:</div>
          {/* Full result list (passing + failing) — same view as the plain
              confirm modal, so what the user sees is consistent. */}
          <ul className='list-unstyled mb-3'>
            {action.guards.map((g) => (
              <li key={g.name} className='text-gray-600 fs-6 mb-1'>
                {g.ok ? (
                  <i className='fa fa-check-circle text-success me-2'></i>
                ) : g.severity === 'block' ? (
                  <i className='fa fa-times-circle text-danger me-2'></i>
                ) : (
                  <i className='fa fa-exclamation-triangle text-warning me-2'></i>
                )}
                {g.message || g.label || g.name}
              </li>
            ))}
          </ul>
          <div className='text-gray-600 fs-6 fw-bold'>
            Do you want to proceed with "{action.label}" anyway?
          </div>
        </div>
      ),
      show: true,
    })
  }

  // Check results rendered inside the confirm modal — shown for passing AND
  // failing checks, so the modal's content is consistent instead of only
  // surfacing problems.
  const guardResultsList = (guards: WorkflowActionModel['guards']) => (
    <ul className='list-unstyled mb-3'>
      {guards.map((g) => (
        <li key={g.name} className='text-gray-600 fs-6 mb-1'>
          {g.ok ? (
            <i className='fa fa-check-circle text-success me-2'></i>
          ) : g.severity === 'block' ? (
            <i className='fa fa-times-circle text-danger me-2'></i>
          ) : (
            <i className='fa fa-exclamation-triangle text-warning me-2'></i>
          )}
          {g.message || g.label || g.name}
        </li>
      ))}
    </ul>
  )

  // A blocked action must still SHOW its check results — a silently disabled
  // option reads as "validation stopped running". No proceed button: the
  // failing block-severity checks have to pass first.
  const openBlockedModal = (action: WorkflowActionModel) => {
    setSettings({
      cancelButtonText: 'Close',
      saveButtonText: '',
      saveButtonCallback: () => {},
      closeButtonCallback: () => {
        setDefaults()
      },
      titleText: `${action.label} — blocked`,
      content: (
        <div>
          <div className='text-gray-700 fs-7 fw-bold mb-2'>Checks:</div>
          {guardResultsList(action.guards)}
          <div className='text-danger fs-6 fw-bold'>
            This action is blocked until the failing checks above pass.
          </div>
        </div>
      ),
      show: true,
    })
  }

  const openConfirmModal = (action: WorkflowActionModel, reason: string) => {
    // action.guards here are FRESH (refetched at selection time), so failing
    // warn-checks are shown IN the confirm modal and acknowledged on save.
    // The server still re-evaluates — a warnings_not_acknowledged 422 (e.g.
    // schedule changed under us) reopens the acknowledge modal with the
    // then-current failures.
    const failingWarnings = action.guards.filter((g) => g.severity === 'warn' && !g.ok)
    setSettings({
      cancelButtonText: 'Close',
      saveButtonText: failingWarnings.length > 0 ? 'Proceed anyway' : action.label,
      saveButtonCallback: () => {
        performWorkflowAction(
          action,
          reason,
          failingWarnings.map((g) => g.name)
        )
      },
      closeButtonCallback: () => {
        setDefaults()
      },
      titleText: action.label,
      content: (
        <div>
          {action.guards.length > 0 && (
            <>
              <div className='text-gray-700 fs-7 fw-bold mb-2'>Checks:</div>
              {guardResultsList(action.guards)}
            </>
          )}
          <div className='text-gray-600 fs-6 fw-bold'>
            {action.confirm || `Are you sure you want to apply "${action.label}" to this schedule?`}
          </div>
        </div>
      ),
      show: true,
    })
  }

  const handleWorkflowSelection = async (actionKey: string) => {
    const stale = workflow?.actions.find((a) => a.key === actionKey)
    if (!stale || !laborScheduleActual) {
      setDefaults()
      return
    }
    // Re-fetch at click time so the modal shows the schedule's CURRENT
    // check results, not the ones from when the dropdown was rendered.
    setLoadingState(true)
    const seq = ++workflowReqSeqRef.current
    const freshWorkflow: ScheduleWorkflowModel | null =
      (await getScheduleWorkflow(laborScheduleActual.id)) || null
    setLoadingState(false)
    if (freshWorkflow && seq === workflowReqSeqRef.current) {
      setWorkflow(freshWorkflow)
    }
    const action = (freshWorkflow?.actions || []).find(
      (a: WorkflowActionModel) => a.key === actionKey
    )
    if (!action) {
      // status changed under us and the action no longer applies — resync the
      // schedule state too, or the badge/gating stays on the stale status
      showToast('Schedule Changed', 'The schedule status changed — options refreshed.', 'warning')
      resyncSchedule()
      setDefaults()
      return
    }
    if (action.blocked) {
      // Show WHY instead of a toast: the checks modal with the failing
      // blockers and no proceed button.
      openBlockedModal(action)
      return
    }
    if (action.requires_reason) {
      // One dialog: the fresh check results, the reason, and the button.
      setForm({kind: 'reason', action})
      return
    }
    // Already fetched fresh above — open the modal directly.
    openConfirmModal(action, '')
  }

  const clearSchedule = async () => {
    if (laborScheduleActual) {
      setLoadingState(true)
      try {
        await clearLaborSchedule(selectedLocationId, laborScheduleActual?.week_start)
        showToast('Schedule Cleared', 'Schedule cleared successfully!', 'success')
        // Mirror the backend reset: a cleared schedule returns to the initial
        // status (open for the default workflow) with no template on record.
        setLaborScheduleActual({
          ...laborScheduleActual,
          workflow_status: 'open',
          template_applied: false,
        })
        handleState([], 'events')
      } catch (e) {
        showToast('Error', 'Error clearing schedule!', 'danger')
      }
      setTimeout(() => {
        setLoadingState(false)
      }, 10)
    }
  }

  const clearAssignments = async () => {
    if (laborScheduleActual) {
      setLoadingState(true)
      try {
        const res = await clearScheduleAssignments(
          selectedLocationId,
          laborScheduleActual?.week_start
        )
        if (!res) {
          throw new Error('clear assignments failed')
        }
        // Reload the week's shifts: every unassigned shift gets its per-shift
        // row with the assign-employee picker back.
        const shifts = await getWeeklyShifts(selectedDate, selectedLocationId)
        handleState(convertLaborShifts(shifts), 'events')
        showToast(
          'Assignments Cleared',
          `${res.cleared_count} shift${res.cleared_count === 1 ? '' : 's'} back to unassigned.`,
          'success'
        )
      } catch (e) {
        showToast('Error', 'Error clearing assignments!', 'danger')
      }
      setTimeout(() => {
        setLoadingState(false)
      }, 10)
    }
  }

  const getPDF = async () => {
    if (laborScheduleActual) {
      setLoadingState(true)
      // get pdf content from api and download
      try {
        await getPDFContent(selectedLocationId, laborScheduleActual?.week_start)
        showToast('PDF Downloaded', 'PDF downloaded successfully!', 'success')
      } catch (e) {
        showToast('Error', 'Error downloading pdf!', 'danger')
      }
      // download pdf content
      setTimeout(() => {
        setLoadingState(false)
      }, 10)
    }
  }

  const copyFromPreviousWeeks = async (copyFromDate: Date) => {
    if (laborScheduleActual && selectedLocation) {
      setLoadingState(true)
      try {
        // copyFromPreviousWeek swallows request errors and returns undefined,
        // so a missing result must be treated as failure here.
        const res = await copyFromPreviousWeek(
          selectedLocationId,
          laborScheduleActual?.week_start,
          copyFromDate
        )
        if (!res) {
          throw new Error('copy request failed')
        }
        // The copy replaces the week's shifts server-side; nothing watches
        // selectedLaborScheduleId, so reload the events directly.
        const shifts = await getWeeklyShifts(selectedDate, selectedLocationId)
        handleState(convertLaborShifts(shifts), 'events')
        // Mirror the backend side effect: copying into an open schedule flips
        // it to in_progress (like a first shift does).
        if (laborScheduleActual.workflow_status === 'open' && res.copied_count > 0) {
          setLaborScheduleActual({...laborScheduleActual, workflow_status: 'in_progress'})
        }
        showToast(
          'Schedule Copied',
          `${res.copied_count} shift${res.copied_count === 1 ? '' : 's'} copied successfully!`,
          'success'
        )
      } catch (e) {
        showToast('Error', 'Error copying schedule!', 'danger')
      }
      setTimeout(() => {
        setLoadingState(false)
      }, 10)
    }
  }

  const validateCurrentSchedule = async () => {
    if (laborScheduleActual && selectedLocation) {
      setLoadingState(true)
      try {
        await validateSchedule(selectedLocationId, laborScheduleActual.week_start).then(
          (res: Array<{rule: string; description: string; check: boolean}>) => {
            showToast(
              'Schedule Validation Completed',
              'Schedule validation completed successfully!',
              'success'
            )
            setSettings({
              cancelButtonText: 'Close',
              saveButtonText: '',
              saveButtonCallback: () => {},
              closeButtonCallback: () => {
                setDefaults()
              },
              titleText: 'Validate Schedule Result',
              // show each result in res as a list
              content: (
                <div>
                  <div className='row'>
                    <div className='col-12'>
                      <div className='form-group'>
                        <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4'>
                          <thead>
                            <tr>
                              <th>Rule</th>
                              <th>Description</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {res.map((value) => (
                              <tr key={value.rule}>
                                <td>{value.rule}</td>
                                <td>{value.description}</td>
                                <td>
                                  {value.check ? (
                                    <i className='fa fa-check-circle text-success'></i>
                                  ) : (
                                    <i className='fa fa-times-circle text-danger'></i>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ),
              show: true,
            })
          }
        )
      } catch (e) {
        showToast('Error', 'Error validating schedule!', 'danger')
        setDefaults()
      }
      setTimeout(() => {
        setLoadingState(false)
      }, 10)
    }
  }

  // Fetch the location's templates when the apply dialog opens.
  const formKind = form?.kind
  useEffect(() => {
    if (formKind !== 'apply_template') return
    let live = true
    setTemplates(null)
    getLaborTemplates(selectedLocationId).then((list) => live && setTemplates(list || []))
    return () => {
      live = false
    }
  }, [formKind, selectedLocationId])

  const applySelectedTemplate = async (template: LaborTemplateListItemModel) => {
    if (!selectedLaborScheduleId || !selectedLocationId || !laborScheduleActual) return
    setLoadingState(true)
    try {
      const payload: {
        location_id: number
        schedule_id: number
        date?: string
        week_start?: string
      } = {
        location_id: selectedLocationId,
        schedule_id: selectedLaborScheduleId,
      }
      if (template.template_type === 'daily') {
        payload.date = format(selectedDate, 'yyyy-MM-dd')
      } else {
        // The schedule's own week_start is authoritative for weekly templates.
        payload.week_start = laborScheduleActual.week_start
      }
      const shifts = await applyLaborTemplate(template.uuid, payload)
      const newEvents = convertLaborShifts(shifts)
      handleState(events.concat(newEvents), 'events')
      // Mirror the backend side effects: the application is recorded (so the
      // gauges flag unstaffed template shifts) and an open schedule flips to
      // in_progress (like a first shift does).
      setLaborScheduleActual({
        ...laborScheduleActual,
        template_applied: true,
        workflow_status:
          laborScheduleActual.workflow_status === 'open'
            ? 'in_progress'
            : laborScheduleActual.workflow_status,
      })
      showToast(
        'Template Applied',
        `${newEvents.length} unassigned shifts added. Assign employees from each shift.`,
        'success'
      )
    } catch (e) {
      showToast('Error', 'Error applying template!', 'danger')
    }
    setTimeout(() => {
      setLoadingState(false)
    }, 10)
  }

  // The result view is the deliverable, not a nicety: partial fill is the
  // normal outcome, and "45 of 47 filled" with no explanation reads as broken.
  const openAutofillResultModal = (result: LaborAutofillResultModel) => {
    const fullyFilled = result.unfilled.length === 0
    setSettings({
      cancelButtonText: 'Close',
      saveButtonText: '',
      saveButtonCallback: () => {},
      closeButtonCallback: () => setDefaults(),
      titleText: 'Auto-fill Results',
      content: (
        <div>
          <div className={`fs-5 fw-bold mb-3 ${fullyFilled ? 'text-success' : 'text-gray-800'}`}>
            {result.filled_count} of {result.open_before} open shift
            {result.open_before === 1 ? '' : 's'} filled
          </div>

          {result.guards.length > 0 && (
            <>
              <div className='text-gray-700 fs-7 fw-bold mb-2'>Checks:</div>
              {guardResultsList(result.guards)}
            </>
          )}

          {/* The cost of covering everything. Grouped by rule so a week with
              nine overtime shifts reads as one decision to review, not nine. */}
          {result.warnings.length > 0 && (
            <>
              <div className='text-gray-700 fs-7 fw-bold mb-2'>
                Needs your review ({result.warnings.length}) — shifts were covered by bending these
                rules:
              </div>
              <ul className='list-unstyled mb-3'>
                {groupAutofillWarnings(result.warnings).map((group) => (
                  <li key={group.reason} className='mb-2'>
                    <span className='text-warning fw-bold fs-7'>
                      <i className='fa fa-exclamation-triangle me-2'></i>
                      {group.label} ({group.lines.length})
                    </span>
                    <ul className='list-unstyled ms-4 mt-1'>
                      {group.lines.slice(0, 5).map((text) => (
                        <li key={text} className='text-gray-600 fs-8'>
                          {text}
                        </li>
                      ))}
                      {group.lines.length > 5 && (
                        <li className='text-gray-500 fs-8'>…and {group.lines.length - 5} more</li>
                      )}
                    </ul>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* The Staffing Rules page, judged against the finished week. Only
              the broken ones are listed; the count says the rest were checked. */}
          {(() => {
            const rules = summarizeStaffingRules(result.staffing_rules)
            if (rules.checked === 0) return null
            if (rules.broken.length === 0) {
              return (
                <div className='text-success fs-7 mb-3'>
                  <i className='fa fa-check me-2'></i>
                  All {rules.checked} staffing rule{rules.checked === 1 ? '' : 's'} kept.
                </div>
              )
            }
            return (
              <>
                <div className='text-gray-700 fs-7 fw-bold mb-2'>
                  Staffing rules not met ({rules.broken.length} of {rules.checked}):
                </div>
                <ul className='list-unstyled mb-3'>
                  {rules.broken.map((rule) => (
                    <li key={rule.slug} className='text-gray-600 fs-7 mb-2'>
                      <span className='fw-bold'>{rule.name}</span>
                      <br />
                      {rule.sentence}
                    </li>
                  ))}
                </ul>
              </>
            )
          })()}

          {result.unfilled.length > 0 && (
            <>
              <div className='text-gray-700 fs-7 fw-bold mb-2'>
                Left open ({result.unfilled.length}) — assign these by hand:
              </div>
              <ul className='list-unstyled mb-3'>
                {result.unfilled.map((u) => (
                  <li key={u.shift_id} className='text-gray-600 fs-7 mb-2'>
                    <span className='fw-bold'>
                      {u.start_time ? format(parseISO(u.start_time), 'EEE HH:mm') : ''}
                      {u.end_time ? `–${format(parseISO(u.end_time), 'HH:mm')}` : ''}
                      {u.position ? ` ${u.position}` : ''}
                    </span>
                    <br />
                    {u.detail}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Auto-fill is the one path with no manager present to enter a
              break, so it derives one — which moves the week's hours and cost
              and therefore has to be stated, not inferred from the totals. */}
          {result.breaks_added > 0 && (
            <div className='text-gray-600 fs-7 mb-2'>
              <i className='fa fa-mug-hot me-2'></i>
              Unpaid break added to {result.breaks_added} shift
              {result.breaks_added === 1 ? '' : 's'} — 30 minutes from 6 hours, 60 from 8.
            </div>
          )}

          {/* A position nobody holds is staffed from the whole roster so a typo
              cannot freeze a week — but that must be visible, not silent. */}
          {result.position_fallbacks.length > 0 && (
            <div className='text-warning fs-7 mb-2'>
              No one holds {result.position_fallbacks.join(', ')} — those shifts were staffed from
              the whole team.
            </div>
          )}

          <div className='text-gray-500 fs-8'>
            Undo with “Clear assignments (keep template)”.
            {result.measure ? ` Ranked on ${result.measure}` : ''}
            {result.scored_employees ? ` (${result.scored_employees} with history)` : ''}.
          </div>
        </div>
      ),
      show: true,
    })
  }

  const runAutofill = async () => {
    if (!laborScheduleActual || !selectedLocationId) return
    // Named, not just spun: the fill scores six weeks of history and takes a
    // couple of seconds, which reads as a hung page without something saying
    // what the wait is for.
    setLoadingState(true, 'Auto-filling the week…')
    try {
      const result = await autoFillSchedule({
        location_id: selectedLocationId,
        schedule_id: laborScheduleActual.id,
      })
      // Reload the week rather than merging: auto-fill touches many shifts at
      // once and the returned rows carry recomputed hours/labour dollars.
      const shifts = await getWeeklyShifts(selectedDate, selectedLocationId)
      handleState(convertLaborShifts(shifts), 'events')
      showToast(
        'Auto-fill Complete',
        `${result.filled_count} of ${result.open_before} open shifts filled.`,
        result.unfilled.length === 0 ? 'success' : 'warning'
      )
      openAutofillResultModal(result)
    } catch (e: any) {
      const status = e?.response?.status
      if (status === 423) {
        showToast(
          'Schedule Locked',
          'This schedule is locked against editing — auto-fill cannot change it.',
          'danger'
        )
        resyncSchedule()
      } else if (status === 403) {
        showToast('Not Allowed', 'You are not allowed to auto-fill this schedule.', 'danger')
      } else {
        showToast('Error', 'Error auto-filling the schedule!', 'danger')
      }
    }
    setTimeout(() => {
      setLoadingState(false)
    }, 10)
  }

  const uploadScheduleFromFile = async (uploadFile: File) => {
    if (laborScheduleActual && selectedLocation) {
      setLoadingState(true)
      try {
        await uploadFromFile(selectedLocationId, laborScheduleActual?.week_start, uploadFile)
        showToast('Schedule Uploaded', 'Schedule uploaded successfully!', 'success')
      } catch (e) {
        showToast('Error', 'Error uploading schedule!', 'danger')
      }
      setTimeout(() => {
        setLoadingState(false)
      }, 10)
    }
  }

  // Status actions are server-driven: one item per action the backend says
  // THIS user may take from the current status. Blocked ones stay listed (a
  // click shows the failing checks); ones with failing warnings carry a ⚠.
  // Validate lives with the schedule actions — it is read-only and must stay
  // available on locked/approved weeks for non-admins, who have no status
  // actions.
  const workflowActions = workflow?.actions || []

  // Kept for the confirm/acknowledge modals' close callbacks: closing one
  // leaves no half-picked action behind.
  const setDefaults = () => setForm(null)

  const confirmDestructive = (
    titleText: string,
    saveButtonText: string,
    content: JSX.Element,
    run: () => void
  ) =>
    setSettings({
      cancelButtonText: 'Cancel',
      saveButtonText,
      saveButtonCallback: run,
      closeButtonCallback: setDefaults,
      titleText,
      content,
      show: true,
    })

  const editable = ['open', 'in_progress'].includes(laborScheduleActual?.workflow_status || '')

  // Every action runs on click. The old Options panel was a form — pick in a
  // select, then press Apply — so even "Download PDF" took three clicks.
  //
  // Layout follows the usual page-header rule (Polaris, Carbon, NN/g): the
  // next workflow step is the primary button, the frequent actions are
  // visible buttons beside it (`toolbar` — only actions that are ALWAYS
  // offered, so the header never shifts as the week's state changes), and
  // the occasional or state-dependent ones sit in a
  // "More" menu with the destructive ones last, behind a divider.
  type MenuAction = {
    key: string
    label: string
    icon: string
    show: boolean
    danger?: boolean
    toolbar?: boolean
    // Toolbar buttons have less room than menu rows.
    shortLabel?: string
    run: () => void | Promise<void>
  }
  const scheduleActions: MenuAction[] = [
    {
      key: 'validate',
      label: 'Validate',
      icon: 'bi-clipboard-check',
      show: true,
      toolbar: true,
      run: validateCurrentSchedule,
    },
    {
      // Offered while the backend still has a recommendation on file for this
      // store. A dismissed one is still on file — rejecting it stops the
      // prompt, not the way back to it — so it stays listed; only an applied
      // week drops out of /pending.
      key: 'recommended_schedule',
      label: `View recommended schedule (week of ${pendingProposal?.week_start})`,
      icon: 'bi-stars',
      show: !!pendingProposal,
      run: openModal,
    },
    {
      // Auto-fill assigns people to shifts that already exist, so it only
      // makes sense once the week HAS unassigned shifts — typically straight
      // after a template application. Gated on edit (it changes existing
      // shifts) rather than create.
      key: 'autofill',
      label: `Auto-fill ${unassignedCount} unassigned shift${unassignedCount === 1 ? '' : 's'}`,
      icon: 'bi-magic',
      show: canEditShifts && editable && unassignedCount > 0,
      run: () =>
        confirmDestructive(
          'Auto-fill Schedule',
          'Auto-fill',
          <div>
            <div className='text-gray-600 fs-6 fw-bold'>
              Assign employees to the {unassignedCount} unassigned shift
              {unassignedCount === 1 ? '' : 's'} in this week?
            </div>
            <div className='text-gray-600 fs-7 mt-2'>
              Shifts already assigned are left alone, and nobody will be double-booked or given two
              shifts in a day.
            </div>
            <div className='text-gray-600 fs-7 mt-2'>
              Covering every shift comes first, so overtime and short turnarounds are allowed where
              there is no other way to staff a shift — each one is listed afterwards for you to
              review.
            </div>
            <div className='text-gray-400 fs-7 mt-2'>
              Undo with “Clear assignments (keep template)”.
            </div>
          </div>,
          runAutofill
        ),
    },
    {
      // Unlike copy-from, applying a template to a still-empty (open) schedule
      // is the primary use case — the backend flips the schedule
      // open→in_progress on apply, like a first shift.
      key: 'apply_template',
      label: 'Apply schedule template…',
      icon: 'bi-layout-three-columns',
      show: canCreateShifts && editable && !!selectedLaborScheduleId,
      run: () => setForm({kind: 'apply_template'}),
    },
    {
      // Open weeks included: copying into a still-empty schedule is the
      // primary use case (backend flips open→in_progress).
      key: 'copy_from',
      label: 'Copy from a previous week…',
      icon: 'bi-files',
      show: canCreateShifts && editable,
      run: () => setForm({kind: 'copy_from'}),
    },
    {
      // The read direction of apply-template, and deliberately NOT gated on
      // workflow status: saving what a locked or approved week looks like is
      // exactly when a manager wants to keep it. It writes only to the
      // template tables, so no week is at risk. Offered while the board has
      // shifts — an empty week has nothing to save, and the backend refuses it.
      key: 'save_as_template',
      label: 'Save as template…',
      icon: 'bi-bookmark-plus',
      show: canCreateShifts && !!laborScheduleActual && events.length > 0,
      run: () => setShowSaveAsTemplate(true),
    },
    {
      key: 'pdf',
      label: 'Download PDF',
      icon: 'bi-file-earmark-pdf',
      show: true,
      toolbar: true,
      shortLabel: 'PDF',
      run: getPDF,
    },
    {
      key: 'upload_file',
      label: 'Upload schedule from file…',
      icon: 'bi-upload',
      show: SHOW_UPLOAD_FROM_FILE && canEditShifts && editable,
      run: () => setForm({kind: 'upload_file'}),
    },
  ]

  const resetActions: MenuAction[] = [
    {
      // Unassign the week's shifts (undo of template assignment) — the
      // template's shifts themselves stay. Only offered while a template
      // application is on record; Clear Schedule removes template and shifts
      // alike.
      key: 'clear_assignments',
      label: 'Clear assignments (keep template)',
      icon: 'bi-person-dash',
      show: canEditShifts && editable && !!laborScheduleActual?.template_applied,
      danger: true,
      run: () =>
        confirmDestructive(
          'Clear Assignments',
          'Clear assignments',
          <div className='text-gray-600 fs-6 fw-bold'>
            Remove the employee from every shift this week? The shifts themselves stay as the
            template laid them out — you can re-assign from the unassigned shifts.
          </div>,
          clearAssignments
        ),
    },
    {
      key: 'clear',
      label: 'Clear schedule',
      icon: 'bi-trash3',
      show: canEditShifts && laborScheduleActual?.workflow_status === 'in_progress',
      danger: true,
      run: () =>
        confirmDestructive(
          'Clear Schedule',
          'Clear Schedule',
          <div>
            <div className='text-gray-600 fs-6 fw-bold'>
              Are you sure you want to clear the schedule?
              {laborScheduleActual?.template_applied && (
                <span> This also removes the applied template's shifts.</span>
              )}
            </div>
            <div className='text-danger fs-7 fw-bold'>This action cannot be undone.</div>
          </div>,
          clearSchedule
        ),
    },
  ]

  const renderItem = (a: MenuAction) => (
    <Dropdown.Item
      key={a.key}
      as='button'
      type='button'
      className={`d-flex align-items-center py-2 px-5 ${a.danger ? 'text-danger' : ''}`}
      onClick={a.run}
    >
      <i className={`bi ${a.icon} fs-5 me-3 ${a.danger ? 'text-danger' : 'text-gray-500'}`}></i>
      <span className='text-wrap'>{a.label}</span>
    </Dropdown.Item>
  )

  const toolbarActions = scheduleActions.filter((a) => a.show && a.toolbar)
  // A toolbar button stays disabled while its request runs, so a double click
  // can't send it twice (two PDF downloads, two validation dialogs).
  const runTool = async (a: MenuAction) => {
    if (toolBusyRef.current) return
    toolBusyRef.current = true
    setToolBusy(a.key)
    try {
      await a.run()
    } finally {
      toolBusyRef.current = false
      setToolBusy(null)
    }
  }
  const moreActions = scheduleActions.filter((a) => a.show && !a.toolbar)
  const visibleReset = resetActions.filter((a) => a.show)

  // Workflow actions are server-driven and usually one or two (send for
  // approval; approve + reject). The primary button is the one that moves the
  // schedule on — forward, else a sideways hand-off (DM → admin) — chosen by
  // the backend's `direction`, never by list order: config order put Reject
  // ahead of Approve and made Reject the blue one. Backward actions (reject,
  // reactivate) are styled as send-backs. Without `direction` (an older
  // backend) nothing is primary rather than guessing.
  //
  // Up to two are buttons, ranked so the primary always gets one; the rest go
  // in a ⋯ menu beside them, never into the tools' More. Shown with the
  // primary last.
  const MAX_STATUS_BUTTONS = 2
  const primaryKey = (
    workflowActions.find((a) => !a.blocked && a.direction === 'forward') ||
    workflowActions.find((a) => !a.blocked && a.direction === 'sideways')
  )?.key
  const rank = (a: WorkflowActionModel) =>
    a.key === primaryKey ? 0 : a.direction === 'backward' ? 2 : 1
  const rankedStatus = [...workflowActions].sort((x, y) => rank(x) - rank(y))
  // Primary last; with no primary the server's own order stands.
  const shownStatus = rankedStatus.slice(0, MAX_STATUS_BUTTONS)
  const statusButtons = primaryKey ? shownStatus.reverse() : shownStatus
  const statusOverflow = rankedStatus.slice(MAX_STATUS_BUTTONS)
  const directionIcon = (action: WorkflowActionModel) =>
    action.direction === 'backward'
      ? 'bi-arrow-counterclockwise'
      : action.key === primaryKey
      ? 'bi-check2-circle'
      : action.direction
      ? 'bi-arrow-right-circle'
      : 'bi-circle'
  const statusButtonClass = (action: WorkflowActionModel) =>
    action.key === primaryKey
      ? 'btn-primary'
      : action.direction === 'backward'
      ? 'btn-light-danger'
      : 'btn-light btn-active-light-primary'

  const statusLabel = (action: WorkflowActionModel) =>
    action.label + (action.requires_reason ? '…' : '')
  const statusIcon = (action: WorkflowActionModel) =>
    action.blocked
      ? 'bi-lock'
      : action.guards.some((g) => g.severity === 'warn' && !g.ok)
      ? 'bi-exclamation-triangle'
      : null
  const statusTitle = (action: WorkflowActionModel) =>
    action.blocked ? 'Blocked — click to see which checks must pass first' : undefined

  // Under the Schedule page's title (see statusSlot.ts); absent on the other
  // tabs, where the group is not shown.
  const statusSlot = useStatusSlot()

  // The status, the actions that change it, and its history — one group, so
  // a status change never reads as one more tool. Template mode keeps the
  // status visible but offers no changes: the real schedule is suspended.
  const statusGroup = (
    <div className='d-flex flex-wrap align-items-center gap-3'>
      <StatusPath
        status={workflow?.status || null}
        fallbackKey={laborScheduleActual?.workflow_status}
      />
      {!templateMode && statusButtons.length > 0 && (
        <div className='d-flex align-items-center gap-2'>
          {statusButtons.map((action) => (
            <button
              key={action.key}
              type='button'
              className={`btn btn-sm text-nowrap ${statusButtonClass(action)}`}
              // Blocked actions still open: the dialog shows which checks
              // must pass first.
              onClick={() => handleWorkflowSelection(action.key)}
              title={statusTitle(action)}
            >
              <i className={`bi ${statusIcon(action) || directionIcon(action)} fs-6`}></i>
              {statusLabel(action)}
            </button>
          ))}
          {statusOverflow.length > 0 && (
            <Dropdown align='start'>
              <Dropdown.Toggle
                id='schedule-more-status-actions'
                size='sm'
                variant='light'
                className='btn-active-light-primary'
                aria-label='More status changes'
              >
                <i className='bi bi-three-dots fs-6 p-0'></i>
              </Dropdown.Toggle>
              <Dropdown.Menu className='py-2'>
                {statusOverflow.map((action) => (
                  <Dropdown.Item
                    key={action.key}
                    as='button'
                    type='button'
                    className='d-flex align-items-center py-2 px-5'
                    onClick={() => handleWorkflowSelection(action.key)}
                  >
                    <i
                      className={`bi ${
                        statusIcon(action) || directionIcon(action)
                      } fs-5 me-3 text-gray-500`}
                    ></i>
                    <span className='text-wrap'>{statusLabel(action)}</span>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      )}
      {!templateMode && strictlyWrongSchedule && (
        <span className='badge badge-light-danger fs-7 fw-semibold py-2 px-3'>
          <i className='bi bi-exclamation-triangle-fill text-danger fs-7 me-2'></i>
          Some time slots are outside the strict staffing limits
        </span>
      )}
      {/* Rendered from the toolbar, outside the page's schedule context: the
          schedule is passed in. */}
      <WorkflowHistoryButton laborScheduleActual={laborScheduleActual} />
    </div>
  )

  return (
    <>
      {statusSlot && createPortal(statusGroup, statusSlot)}
      {/* Portals to #root-modals. The schedule's own week_start is
          authoritative for the weekly target, exactly as it is when applying
          a template. */}
      <SaveScheduleAsTemplateModal
        show={showSaveAsTemplate}
        onHide={() => setShowSaveAsTemplate(false)}
        locationId={selectedLocationId}
        weekStart={
          laborScheduleActual?.week_start
            ? parseISO(laborScheduleActual.week_start)
            : startOfWeek(selectedDate, {weekStartsOn: weekStartOn})
        }
        selectedDate={selectedDate}
        // Timeline is the only day-grained view; on the week grids selectedDate
        // is just the week's anchor, so "the selected day" would silently mean
        // the first day of the week.
        canSaveSingleDay={view === 'timeline'}
      />
      <SchedulerActionForm
        form={form}
        onClose={() => setForm(null)}
        selectedDate={selectedDate}
        weekStartOn={weekStartOn}
        weekStart={laborScheduleActual?.week_start}
        templates={templates}
        // The dialog showed checks fetched when it opened. No second confirm
        // after the reason: the server re-evaluates on post, and a warning or
        // block that appeared meanwhile comes back as a 422 that opens the
        // acknowledge modal / blocked toast in performWorkflowAction.
        onReason={performWorkflowAction}
        onCopyFrom={copyFromPreviousWeeks}
        onApplyTemplate={applySelectedTemplate}
        onUpload={uploadScheduleFromFile}
      />
      {/* Template mode suspends the real schedule behind the draft; its
          own header carries the template actions. */}
      {!templateMode && (
        <div className='d-flex align-items-center gap-2 me-2'>
          {toolbarActions.map((a) => (
            <button
              key={a.key}
              type='button'
              className='btn btn-sm btn-light btn-active-light-primary text-nowrap'
              onClick={() => runTool(a)}
              disabled={toolBusy !== null}
              title={a.label}
            >
              {toolBusy === a.key ? (
                <span className='spinner-border spinner-border-sm me-2' role='status'></span>
              ) : (
                <i className={`bi ${a.icon} fs-6`}></i>
              )}
              {a.shortLabel || a.label}
            </button>
          ))}
          <Dropdown align='end'>
            <Dropdown.Toggle
              id='schedule-more-actions'
              size='sm'
              variant='light'
              className='btn-active-light-primary'
            >
              More
            </Dropdown.Toggle>
            <Dropdown.Menu className='py-2 w-275px'>
              {moreActions.map(renderItem)}
              {visibleReset.length > 0 && (
                <>
                  <Dropdown.Divider />
                  {visibleReset.map(renderItem)}
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )}
    </>
  )
}
